import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { RefreshCw, Ban, Trash2, X, Loader2, CheckCircle2 } from "lucide-react";
import Pager from "@/components/Pager";

export default function AdminOrders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [cancelTarget, setCancelTarget] = useState(null);
  const PER = 20;
  const pages = Math.max(1, Math.ceil(rows.length / PER));
  const view = rows.slice((page - 1) * PER, page * PER);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setRows(data);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const del = async (o) => {
    if (!confirm(`Delete order ${o.external_ref}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${o.id}`);
      toast.success("Order deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  const grant = async (o) => {
    if (!confirm(`Confirm payment for ${o.external_ref} and grant access to ${o.user_name}?\n\nUse this ONLY when you've verified the buyer paid (e.g. gateway receipt). It creates their licence / signals subscription immediately.`)) return;
    try {
      await api.post(`/admin/orders/${o.id}/mark-paid`);
      toast.success("Payment confirmed — access granted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to grant"); }
  };

  const paid = rows.filter((r) => r.status === "paid").length;
  const revenue = rows.filter((r) => r.status === "paid").reduce((s, r) => s + r.total, 0);

  return (
    <div data-testid="admin-orders">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">Admin · Commerce</p>
          <h1 className="font-editorial text-3xl font-medium tracking-tight text-white sm:text-4xl">Orders</h1>
          <p className="mt-2 text-sm text-emerald-100/60">{paid} paid · ${revenue.toFixed(2)} lifetime revenue.</p>
        </div>
        <button onClick={load} className="btn-edge-ghost text-xs"><RefreshCw size={14} /> Refresh</button>
      </div>

      <div className="card-edge overflow-hidden">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-emerald-500/10 text-xs uppercase tracking-widest text-emerald-100/50">
            <tr>
              <th className="px-5 py-3 font-medium">Ref</th>
              <th className="px-5 py-3 font-medium">Customer</th>
              <th className="px-5 py-3 font-medium">Items</th>
              <th className="px-5 py-3 font-medium">Provider</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 text-right font-medium">Total</th>
              <th className="px-5 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-emerald-100/50">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-emerald-100/50">No orders yet.</td></tr>}
            {rows.length > 0 && view.map((o) => (
              <tr key={o.id} className="border-b border-emerald-500/8 last:border-0 hover:bg-emerald-500/[0.03]">
                <td className="px-5 py-3.5 font-mono-edge text-[11px] text-emerald-100/60">{o.external_ref}</td>
                <td className="px-5 py-3.5">
                  <p className="text-white">{o.user_name}</p>
                  <p className="text-[11px] text-emerald-100/50">{o.user_email}</p>
                </td>
                <td className="px-5 py-3.5 text-emerald-100/70">{o.items.map((i) => i.title).join(", ")}</td>
                <td className="px-5 py-3.5 text-emerald-100/70">{o.provider}</td>
                <td className="px-5 py-3.5">
                  <span className={`chip ${
                    o.status === "paid" ? "chip-emerald" :
                    o.status === "failed" ? "chip-amber" :
                    o.status === "cancelled" ? "chip-neutral" : "chip-neutral"
                  }`}>{o.status}</span>
                  {o.access_revoked && <span className="chip chip-amber ml-1">access revoked</span>}
                </td>
                <td className="px-5 py-3.5 text-right font-editorial text-lime-300">${o.total.toFixed(2)}</td>
                <td className="px-5 py-3.5">
                  <div className="flex justify-end gap-2">
                    {o.status !== "paid" && o.status !== "cancelled" && (
                      <button onClick={() => grant(o)} data-testid={`order-grant-${o.id}`} title="Confirm payment & grant access"
                        className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-lime-400/60 hover:text-lime-300">
                        <CheckCircle2 size={14} />
                      </button>
                    )}
                    {o.status !== "cancelled" && (
                      <button onClick={() => setCancelTarget(o)} data-testid={`order-cancel-${o.id}`} title="Cancel order"
                        className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-amber-400/50 hover:text-amber-300">
                        <Ban size={14} />
                      </button>
                    )}
                    <button onClick={() => del(o)} data-testid={`order-delete-${o.id}`}
                      disabled={o.status === "paid"}
                      title={o.status === "paid" ? "Paid orders can't be deleted — cancel instead" : "Delete order"}
                      className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 enabled:hover:border-red-500/40 enabled:hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-30">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pager page={page} pages={pages} total={rows.length} onPage={setPage} testId="admin-orders-pager" />
      </div>

      {cancelTarget && (
        <CancelModal order={cancelTarget} onClose={() => setCancelTarget(null)} onDone={() => { setCancelTarget(null); load(); }} />
      )}
    </div>
  );
}

function CancelModal({ order, onClose, onDone }) {
  const [revoke, setRevoke] = useState(false);
  const [reason, setReason] = useState("customer_request");
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const isPaid = order.status === "paid";

  const escClose = useCallback((e) => { if (e.key === "Escape") onClose(); }, [onClose]);
  useEffect(() => {
    window.addEventListener("keydown", escClose);
    return () => window.removeEventListener("keydown", escClose);
  }, [escClose]);

  const confirmCancel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/cancel`, {
        revoke_access: isPaid && revoke, reason, note, notify,
      });
      toast.success(
        (data.revoked ? "Order cancelled & access revoked" : "Order cancelled") +
        (data.notified ? " — customer notified" : "")
      );
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to cancel"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="card-moss my-8 w-full max-w-md" data-testid="order-cancel-modal">
        <div className="flex items-center justify-between border-b border-emerald-500/12 p-5">
          <div>
            <p className="overline">Cancel order</p>
            <h3 className="mt-1 font-editorial text-lg text-white">{order.external_ref}</h3>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 text-emerald-100/60 hover:bg-white/5 hover:text-white"><X size={16} /></button>
        </div>
        <div className="grid gap-4 p-5">
          <p className="text-sm text-emerald-100/70">
            Cancel <span className="text-white">{order.user_name}</span>'s order for{" "}
            <span className="text-white">{order.items.map((i) => i.title).join(", ")}</span>?
          </p>
          <div>
            <label className="mb-1.5 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">Reason</label>
            <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="order-cancel-reason"
              className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2.5 text-sm text-white">
              <option value="customer_request">Customer request</option>
              <option value="refund">Refunded</option>
              <option value="duplicate">Duplicate order</option>
              <option value="payment_issue">Payment problem</option>
              <option value="fraud">Verification / policy issue</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">Note to customer (optional)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="order-cancel-note"
              placeholder="A short, clear explanation the customer will receive."
              className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white placeholder:text-emerald-100/30 focus:border-emerald-400/60 focus:outline-none" />
          </div>
          <label className="flex items-center gap-2 text-sm text-emerald-100/80">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="order-cancel-notify" className="h-4 w-4 accent-lime-400" />
            <span>Notify the customer by email + Telegram</span>
          </label>
          {isPaid ? (
            <label className="flex items-start gap-2 rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 text-sm text-amber-100">
              <input type="checkbox" checked={revoke} onChange={(e) => setRevoke(e.target.checked)} data-testid="order-cancel-revoke" className="mt-0.5 h-4 w-4 accent-amber-400" />
              <span><span className="font-semibold text-amber-200">Also revoke access</span> — deactivate licences from this order and expire the signals subscription (removing the buyer from Telegram). <span className="text-amber-100/70">Leave unchecked to keep their access.</span></span>
            </label>
          ) : (
            <p className="text-xs text-emerald-100/50">This order isn't paid, so no customer access is affected.</p>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="btn-edge-ghost text-sm">Keep order</button>
            <button onClick={confirmCancel} disabled={busy} data-testid="order-cancel-confirm" className="btn-edge text-sm">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />} Cancel order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
